// src/admin/components/GalleryManager.jsx
// Service pages ke before/after gallery images manage karne ke liye
// Pattern: TestimonialsManager / FAQManager jaisa — server_post_data + FormData + main_id
//
// serviceconnection.js me add karo:
//   export const get_service_gallery_admin = "get_service_gallery_admin";
//   export const save_update_service_gallery = "save_update_service_gallery";
//   export const delete_service_gallery = "delete_service_gallery";

import { useEffect, useState, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";

import {
  server_post_data,
  get_service_gallery_admin,
  save_update_service_gallery,
  delete_service_gallery,
} from "../ServiceConnection/serviceconnection.js";

// Service pages list — servicesData ke slugs se sync rakho
const SERVICE_PAGES = [
  { value: "/services/collision-repair", label: "Collision Repair" },
  {
    value: "/services/auto-painting-refinishing",
    label: "Auto Painting & Refinishing",
  },
  { value: "/services/dent-repair", label: "Dent Repair" },
  {
    value: "/services/frame-structural-repair",
    label: "Frame & Structural Repair",
  },
  { value: "/services/rust-repair", label: "Rust Repair" },
  { value: "/services/insurance-assistance", label: "Insurance Assistance" },
  { value: "/services/private-repairs", label: "Private Repairs" },
  {
    value: "/services/fleet-dealer-services",
    label: "Fleet & Dealer Services",
  },
];

const emptyForm = {
  main_id: 0,
  page_name: SERVICE_PAGES[0].value,
  caption_name: "",
  display_order: 0,
  before_image: null, // File object (naya upload)
  after_image: null,
  before_preview: "", // URL (existing ya local preview)
  after_preview: "",
};

const pageLabel = (value) =>
  SERVICE_PAGES.find((p) => p.value === value)?.label || value;

const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageFilter, setPageFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // ── Fetch list ─────────────────────────────────────────────────────────
  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("page_name", pageFilter);

      const response = await server_post_data(get_service_gallery_admin, fd);
      if (!response.data.error) {
        setItems(response.data.message.gallery_data || []);
      }
    } catch (error) {
      console.error("GalleryManager fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [pageFilter]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  // ── Modal open (add / edit) ────────────────────────────────────────────
  const openAdd = () => {
    setForm({
      ...emptyForm,
      page_name: pageFilter || SERVICE_PAGES[0].value,
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      main_id: item.primary_id,
      page_name: item.page_name,
      caption_name: item.caption_name,
      display_order: item.display_order,
      before_image: null,
      after_image: null,
      before_preview: item.before_image,
      after_preview: item.after_image,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
  };

  // ── Image select + local preview ───────────────────────────────────────
  const handleImageChange = (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({
      ...prev,
      [`${side}_image`]: file,
      [`${side}_preview`]: URL.createObjectURL(file),
    }));
  };

  // ── Save (create / update) ─────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.caption_name.trim()) {
      alert("Caption is required");
      return;
    }
    if (form.main_id === 0 && (!form.before_image || !form.after_image)) {
      alert("Both before and after images are required");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("main_id", form.main_id);
      fd.append("page_name", form.page_name);
      fd.append("caption_name", form.caption_name.trim());
      fd.append("display_order", form.display_order);
      if (form.before_image) fd.append("before_image", form.before_image);
      if (form.after_image) fd.append("after_image", form.after_image);

      const response = await server_post_data(save_update_service_gallery, fd);
      if (!response.data.error) {
        closeModal();
        fetchGallery();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("GalleryManager save error:", error);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete (soft) ──────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const fd = new FormData();
      fd.append("main_id", deleteId);

      const response = await server_post_data(delete_service_gallery, fd);
      if (!response.data.error) {
        setDeleteId(null);
        fetchGallery();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("GalleryManager delete error:", error);
    }
  };

  // ── UI ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6">
      {/* Header row */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Before / After Gallery
          </h1>
          <p className="text-sm text-gray-500">
            Service pages pe dikhne wali before/after images manage karo
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={pageFilter}
            onChange={(e) => setPageFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="">All Services</option>
            {SERVICE_PAGES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>

          <button
            onClick={openAdd}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Images
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-20 text-center">
          <ImageIcon className="mb-3 h-10 w-10 text-gray-300" />
          <p className="font-medium text-gray-500">No gallery images yet</p>
          <p className="text-sm text-gray-400">
            Add before/after pairs to display on service pages
          </p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <div
              key={item.primary_id}
              className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
            >
              <div className="grid grid-cols-2">
                <div className="relative">
                  <img
                    src={item.before_image}
                    alt={`${item.caption_name} before`}
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <span className="absolute left-2 top-2 rounded bg-gray-900/80 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    Before
                  </span>
                </div>
                <div className="relative">
                  <img
                    src={item.after_image}
                    alt={`${item.caption_name} after`}
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <span className="absolute left-2 top-2 rounded bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    After
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between p-4">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-gray-900">
                    {item.caption_name}
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {pageLabel(item.page_name)} · Order {item.display_order}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => openEdit(item)}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteId(item.primary_id)}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add/Edit Modal ─────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {form.main_id === 0
                  ? "Add Gallery Images"
                  : "Edit Gallery Images"}
              </h2>
              <button
                onClick={closeModal}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Service page */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Service Page
                </label>
                <select
                  value={form.page_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, page_name: e.target.value }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  {SERVICE_PAGES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Caption */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Caption
                </label>
                <input
                  type="text"
                  value={form.caption_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, caption_name: e.target.value }))
                  }
                  placeholder="e.g. Dent Repair, Panel Replacement"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Before / After uploads */}
              <div className="grid grid-cols-2 gap-4">
                {["before", "after"].map((side) => (
                  <div key={side}>
                    <label className="mb-1.5 block text-sm font-medium capitalize text-gray-700">
                      {side} Image
                    </label>
                    <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-gray-300 transition-colors hover:border-blue-400">
                      {form[`${side}_preview`] ? (
                        <img
                          src={form[`${side}_preview`]}
                          alt={`${side} preview`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <>
                          <ImageIcon className="mb-1 h-6 w-6 text-gray-300" />
                          <span className="text-xs text-gray-400">
                            Click to upload
                          </span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageChange(e, side)}
                      />
                    </label>
                  </div>
                ))}
              </div>

              {/* Display order */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Display Order
                </label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      display_order: parseInt(e.target.value || "0", 10),
                    }))
                  }
                  className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {form.main_id === 0 ? "Add Images" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ─────────────────────────────────────────────── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-gray-900">
              Delete gallery images?
            </h3>
            <p className="mb-5 text-sm text-gray-500">
              Ye before/after pair service page se hat jayega. This action can
              be undone from the database only.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
